import { useTranslation } from '@rhino-project/core/i18n';
import { useCallback, useMemo, useState } from 'react';

import { useUser, useAccountSettingsPath } from '@rhino-project/core/hooks';
import { useSignOutAction } from '@rhino-project/core/queries';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Dropdown,
  DropdownTrigger,
  DropdownItem,
  DropdownMenu,
  DropdownPopover,
  Label,
  Separator
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useBaseOwnerPath } from '../../hooks';
import { ThemeSwitcher } from './ThemeSwitcher';
import { LanguagePicker } from './LanguagePicker';
import { getLanguageName } from '../../utils/language';

export const AccountMenu = () => {
  const {
    t,
    pickerLocale: locale,
    pickerLocales: availableLocales
  } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { mutate: signOutAction } = useSignOutAction();
  const user = useUser();
  const accountSettingsPath = useAccountSettingsPath();
  const { build } = useBaseOwnerPath();

  const handleSignOut = useCallback(() => {
    if (window.analytics) window.analytics.reset();
    signOutAction();
  }, [signOutAction]);

  const initials = useMemo(() => {
    const name = user?.name || user?.email || '';
    return name
      .split(/[\s@]/)
      .filter(Boolean)
      .map((n: string) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, [user]);

  const items = useMemo(
    () => [
      ...(availableLocales.length > 1
        ? [
            <DropdownItem
              id="language"
              key="language"
              textValue={t('rhino.frontend.language')}
              onAction={() => {
                setMenuOpen(false);
                setPickerOpen(true);
              }}
            >
              <Icon icon="bi:globe2" className="size-4" />
              <Label>{t('rhino.frontend.language')}</Label>
              <span className="ms-auto truncate text-xs text-muted">
                {getLanguageName(locale)}
              </span>
              <Icon icon="bi:chevron-right" className="size-3 shrink-0" />
            </DropdownItem>,
            <Separator key="language-divider" />
          ]
        : []),
      <DropdownItem
        id="settings"
        key="settings"
        textValue={t('rhino.frontend.messages.settings')}
        href={build(accountSettingsPath)}
      >
        <span className="flex items-center gap-2">
          <Icon icon="bi:gear" className="size-4" />
          <Label>{t('rhino.frontend.messages.settings')}</Label>
        </span>
      </DropdownItem>,
      <DropdownItem
        id="logout"
        key="logout"
        textValue={t('rhino.frontend.messages.sign_out')}
        onAction={handleSignOut}
      >
        <span className="flex items-center gap-2">
          <Icon icon="bi:box-arrow-right" className="size-4" />
          <Label>{t('rhino.frontend.messages.sign_out')}</Label>
        </span>
      </DropdownItem>
    ],
    [
      accountSettingsPath,
      availableLocales.length,
      build,
      handleSignOut,
      locale,
      t
    ]
  );

  return (
    <>
      <Dropdown isOpen={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownTrigger>
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar size="sm" color="accent" variant="soft">
              {user?.image && (
                <AvatarImage src={user.image} alt={user?.name || ''} />
              )}
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col items-start">
              <span className="text-sm text-foreground">{user?.email}</span>
              <span className="text-xs text-muted">
                {user?.nickname || user?.name}
              </span>
            </div>
          </div>
        </DropdownTrigger>
        <DropdownPopover
          placement="top start"
          className="w-72 max-w-[calc(100vw-2rem)]"
        >
          <ThemeSwitcher />
          <Separator />
          <DropdownMenu aria-label={t('rhino.frontend.messages.user_actions')}>
            {items}
          </DropdownMenu>
        </DropdownPopover>
      </Dropdown>
      <LanguagePicker isOpen={pickerOpen} onOpenChange={setPickerOpen} />
    </>
  );
};
